import { Select as PomodoroSelect } from '@ifood/pomodoro-components';
import tokens from '@ifood/pomodoro-tokens';
import { useField, useFormikContext } from 'formik';
import React, { Ref, forwardRef } from 'react';
import { useEffectOnce } from 'react-use';
import styled from 'styled-components';

import { useShareForwardedRef } from 'hooks/shared-forward-ref';
import { FieldName } from 'types/form';
import { PropsOf } from 'types/jsx';

export type SelectProps = {
  name: FieldName<string>;
} & PropsOf<typeof PomodoroSelect>;

type PMSelectProps = {
  placeholderActive: boolean;
};

const PMSelect = styled(PomodoroSelect)<PMSelectProps>`
  button {
    color: ${({ placeholderActive }): string => (placeholderActive ? tokens.colors.grayDarker : tokens.colors.black)};
  }
`;

export const Select = forwardRef<HTMLInputElement, SelectProps>(function DefaultSelect(
  { name, items, ...props }: SelectProps,
  ref: Ref<HTMLInputElement>
) {
  const sharedRef = useShareForwardedRef<HTMLInputElement>(ref);

  const formik = useFormikContext();

  const [{ value }, { error, touched }, { setTouched, setValue }] = useField(name);

  // This useEffect is required, so we pick up the values
  // the user typed before the JS finishes loading
  useEffectOnce(() => {
    if (sharedRef.current?.value) {
      setValue(sharedRef.current.value);
      setTouched(true);
    }
  });

  const optionValue = items?.find((option) => option.value === value);

  return (
    <PMSelect
      ref={sharedRef}
      name={name}
      error={touched ? error : undefined}
      value={optionValue}
      placeholderActive={!optionValue}
      items={items}
      onChange={formik.handleChange}
      onBlur={formik.handleBlur}
      {...props}
    />
  );
});
